import type { GetTagsItem } from '@api';

/** The `path` / `depth` slice of the tag catalog, matching the `GET /v1/tags` query params. */
export type TagPathDepthQuery = {
  readonly path?: string;
  readonly depth?: number;
};

/**
 * Client-side mirror of the backend `TagsByPathSpecification`: keeps the descendants of `path`
 * that sit at exactly `depth` levels below it. `depth` defaults to 1 (the API default), `depth: 0`
 * resolves to the node itself, and an absent `path` returns the whole catalog — the backend
 * ignores `depth` in that case too. Input order is preserved, so the server's `ORDER BY path`
 * still holds for every slice.
 *
 * @param tags - Full tag catalog as returned by `GET /v1/tags` without query params.
 * @param query - The `path` / `depth` slice to select.
 * @returns The tags matching the slice, in catalog order.
 */
export function filterTagsByPathDepth(tags: readonly GetTagsItem[], query: TagPathDepthQuery = {}): GetTagsItem[] {
  const { path, depth = 1 } = query;

  if (!path) {
    return [...tags];
  }

  const targetLevel = path.split('.').length + depth;

  return tags.filter(
    (tag) => (tag.path === path || tag.path.startsWith(`${path}.`)) && tag.path.split('.').length === targetLevel,
  );
}
